<template>
    <div class="af" :class="{ 'af-is-open': open }">
        <button v-if="!open" type="button" class="ai-btn af-launch" aria-label="Ask the Forge" @click="openPanel()">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
            <span>Ask the Forge</span>
        </button>
        <section v-else class="af-panel" role="dialog" aria-label="Ask the Forge">
            <header class="af-head">
                <div class="af-title">
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
                    <div><b>Ask the Forge</b><span>{{ companyName }}</span></div>
                </div>
                <div class="af-head-btns">
                    <button v-if="thread.length" type="button" class="af-icon-btn" title="Start a new conversation" aria-label="New conversation" @click="clear()"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
                    <button type="button" class="af-icon-btn" title="Close" aria-label="Close" @click="open = false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
                </div>
            </header>

            <div ref="body" class="af-body">
                <div v-if="!thread.length" class="af-empty">
                    <p class="af-hello">Ask anything about how {{ companyName }} works. The Forge answers from your playbook and shows you where it found it.</p>
                    <div class="af-chips">
                        <span class="af-src-label">Try one of these</span>
                        <button v-for="e in examples" :key="e.q" type="button" class="af-chip" @click="ask(e.q)">{{ e.label }}</button>
                    </div>
                </div>
                <template v-for="(m, i) in thread" :key="i">
                    <div v-if="m.role == 'user'" class="af-q">{{ m.text }}</div>
                    <div v-else class="af-a" :class="{ 'af-a-miss': !m.found }">
                        <div class="af-a-text">
                            <template v-for="(b, j) in blocks(m.text)" :key="j">
                                <ol v-if="b.list" class="af-steps" :start="b.start"><li v-for="(li, k) in b.items" :key="k">{{ li }}</li></ol>
                                <p v-else>{{ b.text }}</p>
                            </template>
                        </div>
                        <div v-if="m.sources && m.sources.length" class="af-src">
                            <span class="af-src-label">From your playbook</span>
                            <button v-for="s in m.sources" :key="s.Slug" type="button" class="af-src-btn" @click="openEntry(s)">
                                <span>{{ s.Name }}</span><em>{{ typeLabel(s.Type) }}</em>
                            </button>
                        </div>
                        <div v-if="m.related && m.related.length" class="af-src af-related">
                            <span class="af-src-label">{{ m.found ? 'Also related' : 'Closest matches' }}</span>
                            <button v-for="s in m.related" :key="s.Slug" type="button" class="af-src-btn af-src-plain" @click="openEntry(s)">
                                <span>{{ s.Name }}</span><em>{{ typeLabel(s.Type) }}</em>
                            </button>
                        </div>
                        <div v-if="i == thread.length - 1 && m.followUps && m.followUps.length" class="af-chips af-follow">
                            <button v-for="q in m.followUps" :key="q" type="button" class="af-chip" @click="ask(q)">{{ q }}</button>
                        </div>
                    </div>
                </template>
                <div v-if="busy" class="af-thinking" role="status"><span></span><span></span><span></span> Reading your playbook…</div>
                <div v-if="error" class="af-error" role="alert">{{ error }}</div>
            </div>

            <form class="af-input" @submit.prevent="ask(question)">
                <textarea id="af-question" ref="input" v-model="question" rows="1" maxlength="1000" placeholder="Ask a question…" aria-label="Your question" @keydown.enter.exact.prevent="ask(question)"></textarea>
                <ai-dictation class="af-mic" @text="t => question = (question ? question.trimEnd() + ' ' : '') + t" />
                <button type="submit" class="ai-btn af-send" :class="{ 'disable-button': busy || !question.trim() }" aria-label="Ask"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h14M13 6l6 6-6 6"/></svg></button>
            </form>
            <footer class="af-foot">
                <span>Answers come from your company's playbook.</span>
                <router-link v-if="canForge" to="/ai-hub" @click="closeOnPhone()">Open the Forge</router-link>
            </footer>
        </section>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AIDictation from './AIDictation.vue';

const TYPE_LABELS: any = { PROCESS: 'Process', CHECKLIST: 'Checklist', SHEET: 'Role Sheet', TIMELINE: 'Value Engine', VIDEO: 'Video', LINK: 'Link' };
const STORE_KEY = 'askForgeThread';

// Ask the Forge (App.vue, every page, every login): questions about the company's playbook, answered by
// POST /api/ask from the entries this person can open. The conversation stays while they move around the app.
export default defineComponent({
    name: 'AskForge',
    components: { 'ai-dictation': AIDictation },
    data() {
        return { open: false, question: '', thread: [] as any[], busy: false, error: '' };
    },
    computed: {
        user(): any { return this.$store.state.user || {}; },
        companyName(): string { return this.user.companyName || 'your company'; },
        canForge(): boolean { return !!this.user.isAdmin && [ 'Admin', 'Editor' ].includes(this.user.role); },
        // Starters: a few processes this person can see, spread across the list
        examples(): any[] {
            const nodes = (this.$store.state.allNodes || []).filter((n: any) => n.Type == 'PROCESS' && n.Name && n.Name.length < 48 && !/video|test/i.test(n.Name));
            const picks: any[] = [];
            const step = Math.max(1, Math.floor(nodes.length / 3));
            for(let i = Math.floor(step / 2); i < nodes.length && picks.length < 3; i += step) {
                const name = String(nodes[i].Name).trim();
                picks.push({ label: name, q: `Walk me through "${name}".` });
            }
            return picks.length ? picks : [ { label: 'What should I do on my first day?', q: 'What should I do on my first day?' } ];
        }
    },
    watch: {
        thread: { deep: true, handler() { try { sessionStorage.setItem(STORE_KEY + this.user.companyID, JSON.stringify(this.thread.slice(-20))); } catch(e) { /* not kept */ } } }
    },
    created() {
        try { this.thread = JSON.parse(sessionStorage.getItem(STORE_KEY + this.user.companyID) || '[]') || []; } catch(e) { this.thread = []; }
    },
    methods: {
        typeLabel(t: string): string { return TYPE_LABELS[t] || t; },
        // Paragraphs, and numbered steps as a list
        blocks(text: string): any[] {
            const out: any[] = [];
            for(const line of String(text || '').split(/\n+/).map(l => l.trim()).filter(Boolean)) {
                const m = line.match(/^(\d+)[.)]\s+(.*)$/);
                const last = out[out.length - 1];
                if(m && last && last.list) last.items.push(m[2]);
                else if(m) out.push({ list: true, start: Number(m[1]), items: [ m[2] ] });
                else out.push({ text: line.replace(/^[-•]\s+/, '') });
            }
            return out;
        },
        openPanel() {
            this.open = true;
            this.$nextTick(() => { this.scrollDown(); (this.$refs.input as any)?.focus(); });
        },
        closeOnPhone() { if(window.innerWidth <= 640) this.open = false; },
        scrollDown() { const b = this.$refs.body as any; if(b) b.scrollTop = b.scrollHeight; },
        clear() { this.thread = []; this.error = ''; this.question = ''; },
        async ask(q: string) {
            const question = String(q || '').trim();
            if(!question || this.busy) return;
            this.error = '';
            const history = this.thread.slice(-6).map((m: any) => ({ role: m.role, text: m.text }));
            this.thread.push({ role: 'user', text: question });
            this.question = '';
            this.busy = true;
            this.$nextTick(() => this.scrollDown());
            try {
                const r = (await axios.post(`${this.$store.state.apiUrl}/api/ask`, { question, history })).data;
                this.thread.push({ role: 'assistant', text: r.answer, found: r.found, sources: r.sources || [], related: r.related || [], followUps: r.followUps || [] });
            } catch(e: any) {
                const d = e?.response?.data;
                this.error = typeof d == 'string' && d && d.length < 300 ? d : 'The Forge couldn\'t answer that. Please try again.';
                this.thread.pop();
                this.question = question;
            }
            this.busy = false;
            this.$nextTick(() => this.scrollDown());
        },
        openEntry(s: any) {
            if(s.Type == 'LINK' && s.Link) { window.open(s.Link, '_blank'); return; }
            this.closeOnPhone();
            this.$router.push('/playbook/' + s.Slug);
        }
    }
});
</script>

<style scoped>
.af { position: fixed; right: 20px; bottom: 20px; z-index: 900; }
.af-launch { border-radius: 999px; padding: 11px 18px 11px 15px; font-size: 14.5px; font-weight: 700; box-shadow: var(--ai-shadow-lg); }

.af-panel { width: 400px; height: min(640px, calc(100vh - 100px)); background: #fff; border: 1px solid #eadfd5; border-radius: 16px; box-shadow: 0 18px 50px rgba(40, 26, 14, .22); display: flex; flex-direction: column; overflow: hidden; }
.af-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 12px 12px 12px 16px; background: var(--ai-grad); color: #fff; }
.af-title { display: flex; align-items: center; gap: 10px; min-width: 0; }
.af-title div { display: grid; line-height: 1.2; min-width: 0; }
.af-title b { font-size: 15px; }
.af-title span { font-size: 12px; opacity: .9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.af-head-btns { display: flex; gap: 4px; }
.af-icon-btn { width: 30px; height: 30px; border: 0; border-radius: 8px; background: rgba(255, 255, 255, .16); color: #fff; display: grid; place-items: center; cursor: pointer; padding: 0; }
.af-icon-btn:hover, .af-icon-btn:focus-visible { background: rgba(255, 255, 255, .3); outline: none; }
.af-icon-btn svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 2.4; stroke-linecap: round; }

.af-body { flex: 1; overflow-y: auto; padding: 14px 16px; display: flex; flex-direction: column; gap: 12px; font-size: 14.5px; line-height: 1.5; color: #2b2420; }
.af-empty { display: grid; gap: 12px; }
.af-empty .af-src-label { margin-top: 2px; }
.af-hello { margin: 0; color: #5d5148; }
.af-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.af-chip { border: 1px solid var(--ai-border-soft); background: #fff8f1; color: var(--ai-accent-deep); border-radius: 999px; padding: 6px 12px; font-size: 13px; font-weight: 600; cursor: pointer; text-align: left; line-height: 1.3; }
.af-chip:hover, .af-chip:focus-visible { background: #fdecdc; outline: none; }
.af-q { align-self: flex-end; max-width: 85%; background: #f4efe9; border-radius: 14px 14px 3px 14px; padding: 8px 12px; white-space: pre-wrap; overflow-wrap: anywhere; }
.af-a { align-self: stretch; border: 1px solid #efe5dc; border-radius: 14px 14px 14px 3px; padding: 10px 12px; display: grid; gap: 10px; }
.af-a-miss { background: #fbf8f5; }
.af-a-text { display: grid; gap: 6px; }
.af-a-text p { margin: 0; overflow-wrap: anywhere; }
.af-steps { margin: 0; padding-left: 20px; display: grid; gap: 4px; }
.af-src { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.af-src-label { width: 100%; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #8a7f75; }
.af-src-btn { border: 0; background: #eaf0f8; color: #1d4f91; border-radius: 8px; padding: 5px 9px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; gap: 6px; align-items: baseline; text-align: left; max-width: 100%; }
.af-src-btn span { overflow: hidden; text-overflow: ellipsis; }
.af-src-btn em { font-style: normal; font-weight: 400; font-size: 11.5px; opacity: .75; white-space: nowrap; }
.af-src-btn:hover, .af-src-btn:focus-visible { background: #dbe6f4; outline: none; }
.af-src-plain { background: #f4efe9; color: #4a3f37; }
.af-src-plain:hover, .af-src-plain:focus-visible { background: #ebe3da; }
.af-thinking { color: #8a7f75; font-size: 13.5px; display: flex; align-items: center; gap: 4px; }
.af-thinking span { width: 6px; height: 6px; border-radius: 50%; background: var(--ai-accent); animation: af-dot 1s infinite ease-in-out; }
.af-thinking span:nth-child(2) { animation-delay: .15s; }
.af-thinking span:nth-child(3) { animation-delay: .3s; margin-right: 6px; }
@keyframes af-dot { 0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .af-thinking span { animation: none; } }
.af-error { color: #a52c26; font-size: 13.5px; }

.af-input { display: flex; align-items: flex-end; gap: 8px; padding: 10px 12px; border-top: 1px solid #efe5dc; }
.af-input textarea { flex: 1; min-width: 0; margin: 0; resize: none; field-sizing: content; min-height: 38px; max-height: 120px; height: auto !important; padding: 8px 10px; font-size: 14.5px; line-height: 1.4; border: 1px solid #e3d6ca; border-radius: 10px; font-family: inherit; }
.af-input textarea:focus { outline: 2px solid var(--ai-border-soft); border-color: var(--ai-accent); }
.af-mic { flex-shrink: 0; align-self: center; }
.af-send { width: 38px; height: 38px; padding: 0; justify-content: center; border-radius: 10px; flex-shrink: 0; }
.af-send svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
.af-foot { display: flex; justify-content: space-between; gap: 10px; padding: 0 14px 10px; font-size: 12px; color: #8a7f75; }
.af-foot a { color: var(--ai-accent-deep); font-weight: 700; white-space: nowrap; }

@media (max-width: 640px) {
    .af { right: 14px; bottom: 14px; }
    .af-launch span { display: none; }
    .af-launch { padding: 13px; }
    .af.af-is-open { inset: 0; z-index: 10000; } /* over the phone menu button (9999), under the open menu */
    .af-panel { width: 100%; height: 100%; border-radius: 0; border: 0; padding-bottom: env(safe-area-inset-bottom, 0px); }
}
@media print { .af { display: none; } }
</style>
